import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'hardcore-gear');
  const category = getCategoryBySlug(activeTab);
  const articles = getArticlesByCategory(activeTab);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: category?.name || '分类', href: `/category/${activeTab}` },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 md:mb-12">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
                {category?.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                {category?.description}
              </p>
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-8 md:mb-10 scrollbar-hide px-1 py-1 bg-background-100 rounded-full">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 flex flex-col"
                  >
                    <div className="overflow-hidden h-48 md:h-52">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs text-foreground-400">{article.publishDate}</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                        </div>
                        <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <img
                          src={article.authorAvatar}
                          alt={article.author}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                        <span>{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}